import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { EmbedBrandBar, EmbedComposeButton } from "@/components/EmbedChrome";
import {
  CarouselPreviewFeedList,
  HideSensitiveContentContext,
  ImageStyleContext,
  MasonryPreviewFeedList,
  PreviewFeedList,
} from "@/components/PreviewFeed";
import { getCurrentFeedOptions } from "@/lib/api";
import { isHiddenByLabel } from "@/lib/moderation";
import { cn } from "@/lib/utils";

type EmbedMode = "masonry-infinite" | "masony-click" | "carousel" | "default";

type BrandBarPosition = "top" | "bottom" | "off";

const DEFAULT_BRAND_BAR_LABEL = "Build your own feed with";

/**
 * Colours and type the embedder can override from the iframe URL. The CSS
 * reads `--color-feed-*`; these names have to match it exactly or the override
 * silently does nothing.
 */
const STYLE_PARAMS = {
  bgColor: "--color-feed-bg-color",
  primaryTextColor: "--color-feed-primary-text-color",
  secondaryTextColor: "--color-feed-secondary-text-color",
  replyHeaderBgColor: "--color-feed-reply-header-bg-color",
  replyHeaderTextColor: "--color-feed-reply-header-text-color",
} as const;

function FeedEmbedImpl({
  feedId,
  fullscreen = true,
  mode,
  frameless,
  limit,
  className,
  brandBar,
  brandBarLabel,
  compose,
  composeText,
  showSensitive,
}: {
  feedId: string;
  fullscreen: boolean | undefined;
  mode?: EmbedMode;
  frameless?: boolean;
  limit?: number;
  className?: string;
  brandBar: BrandBarPosition;
  brandBarLabel: string;
  compose: boolean;
  composeText?: string;
  showSensitive: boolean;
}) {
  const id = parseInt(feedId);
  const {
    data: preview,
    error,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery(getCurrentFeedOptions(id, limit ?? 20, false));
  const allPosts = useMemo(
    () =>
      (preview ? preview.pages.flatMap((d) => d.posts) : []).filter(
        // An embed is an unauthenticated view on somebody else's site, so a
        // post labelled to be hidden from one never renders here at all.
        (post) => !post.hidden && !isHiddenByLabel(post),
      ),
    [preview],
  );
  const isVideoFeed = allPosts.every((post) =>
    post.record.embed?.$type.includes("video"),
  );

  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    if (allPosts.length > 0) return;

    const interval = setInterval(() => {
      refetchRef.current().catch(console.error);
    }, 15_000);

    return () => clearInterval(interval);
  }, [allPosts.length]);

  const [fontFamily, setFontFamily] = useState<string | undefined>(undefined);

  useLayoutEffect(() => {
    const params = new URLSearchParams(window.location.search);

    for (const [param, property] of Object.entries(STYLE_PARAMS)) {
      const value = params.get(param);

      // Only set it when it was actually passed: an empty custom property is
      // not the same as an absent one, and would defeat the CSS fallback.
      if (value) {
        document.body.style.setProperty(property, value);
      }
    }

    const fontFamily = params.get("fontFamily");

    if (fontFamily) {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect, react-hooks/set-state-in-effect
      setFontFamily(fontFamily);

      // Set on <body>, not <html>: styles.css declares this variable on body
      // itself, and that declaration would shadow one made further up.
      document.body.style.setProperty("--color-feed-font-family", fontFamily);
    }
  }, []);

  const list = mode?.includes("masonry") ? (
    <MasonryPreviewFeedList
      posts={allPosts}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      isLoading={isLoading}
      isVideo={isVideoFeed}
      mode={mode?.includes("infinite") ? "infinite" : "click"}
      frameless={frameless}
    />
  ) : mode?.includes("carousel") ? (
    <CarouselPreviewFeedList
      posts={allPosts}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      isLoading={isLoading}
      isVideo={isVideoFeed}
      mode={mode?.includes("auto") ? "auto" : "scroll"}
      frameless={frameless}
    />
  ) : (
    <PreviewFeedList
      feedId={id}
      posts={allPosts}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      isLoading={isLoading}
      error={error}
      isVideo={isVideoFeed}
      virtualization={fullscreen ? "window" : "scroll"}
      showPostActions={false}
      frameless={frameless}
      className={className}
    />
  );

  if (!fullscreen) {
    return (
      <HideSensitiveContentContext value={!showSensitive}>
        {list}
      </HideSensitiveContentContext>
    );
  }

  return (
    <HideSensitiveContentContext value={!showSensitive}>
      {fontFamily && (
        <link
          rel="stylesheet"
          href={`https://fonts.googleapis.com/css?family=${fontFamily}`}
        />
      )}
      <div
        className={cn(
          "text-slate-normal-overridable flex h-full min-h-0 flex-1 flex-col",
          frameless ? "bg-transparent" : "bg-slate-app-overridable",
        )}
      >
        {brandBar === "top" && (
          <EmbedBrandBar
            position="top"
            label={brandBarLabel}
            href="https://www.graze.social/"
          />
        )}
        {list}
        {compose && (
          <EmbedComposeButton
            text={composeText}
            clearsBottomBar={brandBar === "bottom"}
          />
        )}
        {brandBar === "bottom" && (
          <EmbedBrandBar
            position="bottom"
            label={brandBarLabel}
            href="https://www.graze.social/"
          />
        )}
      </div>
    </HideSensitiveContentContext>
  );
}

export function FeedEmbed({
  feedId,
  fullscreen = true,
  mode: modeProp,
  frameless: framelessProp,
  limit: limitProp,
  className,
}: {
  feedId: string;
  fullscreen?: boolean;
  mode?: EmbedMode;
  frameless?: boolean;
  limit?: number;
  className?: string;
}) {
  const searchParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  const mode = modeProp ?? (searchParams.get("mode") as EmbedMode);
  const frameless = framelessProp ?? searchParams.get("frameless") === "1";
  const limit =
    limitProp ??
    (searchParams.get("limit")
      ? parseInt(searchParams.get("limit")!)
      : undefined);
  const imageStyle = searchParams.get("imageStyle") === "1";

  // The attribution bar and the compose button are chrome around a standalone
  // embed. A frameless embed is content dropped inside the host's own layout —
  // including our own marketing pages — so it gets neither.
  const chrome = fullscreen && !frameless;
  const brandBarParam = searchParams.get("brandBar");
  const brandBar: BrandBarPosition = !chrome
    ? "off"
    : brandBarParam === "top" || brandBarParam === "off"
      ? brandBarParam
      : "bottom";
  const brandBarLabel =
    searchParams.get("brandBarLabel") ?? DEFAULT_BRAND_BAR_LABEL;
  const compose = chrome && searchParams.get("compose") !== "0";
  const composeText = searchParams.get("composeText") ?? undefined;

  // Embeds are unauthenticated and sit on somebody else's page, so labelled
  // media is covered unless the embedder has deliberately opted out.
  const showSensitive = searchParams.get("showSensitive") === "1";

  return (
    <ImageStyleContext value={imageStyle ?? false}>
      <FeedEmbedImpl
        mode={mode}
        feedId={feedId}
        fullscreen={fullscreen}
        frameless={frameless}
        limit={limit}
        className={className}
        brandBar={brandBar}
        brandBarLabel={brandBarLabel}
        compose={compose}
        composeText={composeText}
        showSensitive={showSensitive}
      />
    </ImageStyleContext>
  );
}
